(function () {
  'use strict';

  const ROUTES = {
    discount: '/discount/',
    cartUpdate: '/cart/update.js',
    cart: '/cart.js',
  };

  const MSG = {
    success: window.cartStrings?.discountApplied  || 'Discount applied!',
    invalid: window.cartStrings?.discountInvalid  || 'This discount code is not valid.',
    minimum: window.cartStrings?.discountMinimum  || 'Your order doesn\'t meet the minimum purchase requirement for this discount.',
    generic: window.cartStrings?.discountGeneric  || 'Unable to apply discount. Please try again.',
    empty:   window.cartStrings?.discountEmpty    || 'Please enter a discount code.',
    removed: window.cartStrings?.discountRemoved  || 'Discount removed.',
  };

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function escHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function fmtMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.themeVariables?.moneyFormat || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // ─── Context ─────────────────────────────────────────────────────────────────

  function getContext(el) {
    const root = el.closest('[data-cart-discount]') || document;
    return {
      $input:   root.querySelector('[data-discount-input]'),
      $apply:   root.querySelector('[data-discount-apply]'),
      $message: root.querySelector('[data-discount-message]'),
      $tags:    root.querySelector('[data-discount-tags]'),
    };
  }

  // ─── UI helpers ──────────────────────────────────────────────────────────────

  function setLoading(ctx, loading) {
    const { $input, $apply } = ctx;
    if (!$apply || !$input) return;
    const $text   = $apply.querySelector('.cart-discount__button-text');
    const $loader = $apply.querySelector('.cart-discount__button-loader');
    $apply.disabled = loading;
    $input.disabled = loading;
    if ($text)   $text.hidden   = loading;
    if ($loader) $loader.hidden = !loading;
  }

  function showMessage(ctx, text, type) {
    const { $message, $input } = ctx;
    if (!$message) return;
    $message.textContent = text;
    $message.className   = `cart-discount__message cart-discount__message--${type}`;
    $message.hidden      = false;
    if ($input) $input.classList.toggle('is-error', type === 'error');
    if (type === 'success') {
      clearTimeout($message._timer);
      $message._timer = setTimeout(() => { $message.hidden = true; }, 5000);
    }
  }

  function hideMessage(ctx) {
    const { $message, $input } = ctx;
    if ($message) $message.hidden = true;
    if ($input)   $input.classList.remove('is-error');
  }

  // ─── Session storage ─────────────────────────────────────────────────────────

  function getStoredCodes() {
    try { return JSON.parse(sessionStorage.getItem('_discountCodes') || '[]'); }
    catch { return []; }
  }

  function saveStoredCodes(codes) {
    try { sessionStorage.setItem('_discountCodes', JSON.stringify(codes)); }
    catch {}
  }

  function addStoredCode(code) {
    const codes = getStoredCodes();
    if (!codes.includes(code)) { codes.push(code); saveStoredCodes(codes); }
  }

  function removeStoredCode(code) {
    saveStoredCodes(getStoredCodes().filter(c => c !== code));
  }

  // ─── API ─────────────────────────────────────────────────────────────────────

  async function fetchCart() {
    const res = await fetch(`${ROUTES.cart}?t=${Date.now()}`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  }

  async function checkDiscountExists(code) {
    try {
      const res = await fetch(
        `${ROUTES.discount}${encodeURIComponent(code)}`,
        { method: 'GET', credentials: 'include', redirect: 'follow' }
      );
      const finalUrl = (res.url || '').toLowerCase();
      const encodedLower = encodeURIComponent(code).toLowerCase();
      const codeLower = code.toLowerCase();

      if (
        finalUrl.includes('/discount/' + encodedLower) ||
        finalUrl.includes('/discount/' + codeLower)
      ) {
        return false; 
      }

      return true;
    } catch (_) {
      return true;
    }
  }

  async function applyViaCartUpdate(code) {
    const res = await fetch(ROUTES.cartUpdate, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: code }),
    });
    if (!res.ok) throw new Error('cart/update.js failed: ' + res.status);
    return res.json();
  }

  async function clearCartDiscount() {
    try {
      await fetch(ROUTES.cartUpdate, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' }),
      });
    } catch (_) {}
  }

  async function reapplyLastCode(codes) {
    if (!codes.length) { await clearCartDiscount(); return; }
    for (const c of codes) {
      await fetch(ROUTES.cartUpdate, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: c }),
      }).catch(() => {});
    }
  }

  async function syncCartAttribute(codes) {
    await fetch(ROUTES.cartUpdate, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _discount_codes: codes.join(',') } }),
    });
  }


  async function validateAndApply(code) {

    await applyViaCartUpdate(code);
    const cart = await fetchCart();
    const upper = code.toUpperCase();

    const apps = [
      ...(cart.discount_applications || []),
      ...(cart.cart_level_discount_applications || []),
    ];
    const found = apps.find(d =>
      (d.code || d.title || '').toUpperCase() === upper
    );
    if (found) {
      return { status: 'allocated', amount: found.total_allocated_amount || 0, cart };
    }

    if ((cart.discount_code || '').toUpperCase() === upper) {
      return { status: 'accepted', amount: 0, cart };
    }


    const codeExists = await checkDiscountExists(code);

    if (codeExists) {

      return { status: 'pending', amount: 0, cart };
    }

    return { status: 'invalid', amount: 0, cart };
  }


  function renderTag($tags, code, amount) {
    if (!$tags) return;
    const existing = $tags.querySelector(`[data-code="${CSS.escape(code)}"]`);
    if (existing) existing.remove();

    const fmt = amount ? ` (-${fmtMoney(amount)})` : '';
    const tag = document.createElement('div');
    tag.className    = 'cart-discount__tag';
    tag.dataset.code = code;
    tag.innerHTML = `
      <span class="cart-discount__tag-icon" aria-hidden="true">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M1 6.5L4 9.5L11 2.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
      <span class="cart-discount__tag-code">${escHtml(code)}</span>
      <span class="cart-discount__tag-amount">${escHtml(fmt)}</span>
      <button type="button" class="cart-discount__tag-remove"
        data-remove-discount="${escHtml(code)}"
        aria-label="Remove discount ${escHtml(code)}">
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>`;
    $tags.appendChild(tag);
  }

  function renderTagEverywhere(code, amount) {
    document.querySelectorAll('[data-discount-tags]').forEach($tags => {
      renderTag($tags, code, amount);
    });
  }

  function removeTagEverywhere(code) {
    document.querySelectorAll(`[data-discount-tags] [data-code="${CSS.escape(code)}"]`)
      .forEach(el => el.remove());
  }

  function restoreTagsInto($tags) {
    if (!$tags) return;
    $tags.innerHTML = '';
    getStoredCodes().forEach(code => renderTag($tags, code, 0));
  }

  // ─── Apply flow ─────────────────────────────────────────────────────────────

  async function handleApply(triggerEl) {
    const ctx = getContext(triggerEl);
    const { $input } = ctx;
    if (!$input) return;

    const code = $input.value.trim().toUpperCase();
    if (!code) {
      showMessage(ctx, MSG.empty, 'error');
      $input.focus();
      return;
    }

    if (getStoredCodes().includes(code)) {
      showMessage(ctx, MSG.success, 'success');
      $input.value = '';
      return;
    }

    hideMessage(ctx);
    setLoading(ctx, true);

    try {
      const { status, amount, cart } = await validateAndApply(code);

      if (status === 'invalid') {
        await clearCartDiscount();
        await reapplyLastCode(getStoredCodes());
        showMessage(ctx, MSG.invalid, 'error');
        $input.select();
        return;
      }

      if (status === 'pending') {

        addStoredCode(code);
        await syncCartAttribute(getStoredCodes());
        $input.value = '';
        renderTagEverywhere(code, 0);
        showMessage(ctx, MSG.minimum, 'info');
        dispatchCartUpdate(cart);
        return;
      }

      // status === 'allocated' or 'accepted'
      addStoredCode(code);
      await syncCartAttribute(getStoredCodes());
      $input.value = '';
      renderTagEverywhere(code, amount);
      showMessage(ctx, MSG.success, 'success');
      dispatchCartUpdate(cart);

    } catch (err) {
      console.error('[cart-discount] Apply error:', err);
      showMessage(ctx, MSG.generic, 'error');
    } finally {
      setLoading(ctx, false);
    }
  }

  // ─── Remove flow ────────────────────────────────────────────────────────────

  async function handleRemove(triggerEl) {
    const code  = triggerEl.dataset.removeDiscount;
    const tagEl = triggerEl.closest('.cart-discount__tag');
    if (!code || !tagEl) return;

    document.querySelectorAll(`[data-discount-tags] [data-code="${CSS.escape(code)}"]`)
      .forEach(el => {
        el.style.opacity       = '0.4';
        el.style.pointerEvents = 'none';
      });

    const ctx = getContext(triggerEl);

    try {
      removeStoredCode(code);
      await reapplyLastCode(getStoredCodes());
      await syncCartAttribute(getStoredCodes());
      removeTagEverywhere(code);
      showMessage(ctx, MSG.removed, 'success');
      dispatchCartUpdate();
    } catch (err) {
      console.error('[cart-discount] Remove error:', err);
      document.querySelectorAll(`[data-discount-tags] [data-code="${CSS.escape(code)}"]`)
        .forEach(el => {
          el.style.opacity       = '';
          el.style.pointerEvents = '';
        });
      showMessage(ctx, MSG.generic, 'error');
    }
  }

  // ─── Cart update event ───────────────────────────────────────────────────────

  function dispatchCartUpdate(cartData) {
    ['cart:refresh', 'cart:updated', 'cart-update'].forEach(name => {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: cartData }));
    });
    const section = document.querySelector('[data-section-type="cart-drawer"]');
    if (section) {
      document.dispatchEvent(new CustomEvent('sections:rerender', {
        bubbles: true,
        detail: { section: section.dataset.sectionId || 'cart-drawer' },
      }));
    }
  }

  // ─── Accordion toggle ────────────────────────────────────────────────────────

  function handleToggle(triggerEl, event) {
    if (event && event.target.matches('[data-discount-input]')) return;
    const accordion = triggerEl.closest('.cart-discount');
    if (!accordion) return;
    const content = accordion.querySelector('.cart-discount__content');
    if (!content) return;
    const isOpen = accordion.classList.contains('is-open');
    if (isOpen) {
      content.style.height = content.scrollHeight + 'px';
      content.offsetHeight;
      content.style.height = '0';
      accordion.classList.remove('is-open');
      triggerEl.setAttribute('aria-expanded', 'false');
    } else {
      content.style.height = content.scrollHeight + 'px';
      accordion.classList.add('is-open');
      triggerEl.setAttribute('aria-expanded', 'true');
      content.addEventListener('transitionend', function onEnd() {
        content.removeEventListener('transitionend', onEnd);
        if (accordion.classList.contains('is-open')) content.style.height = 'auto';
      });
    }
  }

  // ─── Observe drawer reinjection ──────────────────────────────────────────────

  function observeTagContainers() {
    const observer = new MutationObserver(() => {
      document.querySelectorAll('[data-discount-tags]').forEach($tags => {
        if (!$tags.dataset.restored) {
          $tags.dataset.restored = '1';
          restoreTagsInto($tags);
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ─── Init ────────────────────────────────────────────────────────────────────

  function init() {
    if (window._cartDiscountInited) return;
    window._cartDiscountInited = true;

    document.querySelectorAll('.cart-discount__content').forEach(content => {
      content.style.height   = '0';
      content.style.overflow = 'hidden';
      if (!content.style.transition) content.style.transition = 'height 0.3s ease';
    });

    document.querySelectorAll('[data-discount-tags]').forEach($tags => {
      $tags.dataset.restored = '1';
      restoreTagsInto($tags);
    });

    const stored = getStoredCodes();
    if (stored.length) reapplyLastCode(stored).catch(() => {});

    observeTagContainers();

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-discount-apply]')) {
        handleApply(e.target.closest('[data-discount-apply]'));
        return;
      }
      const removeBtn = e.target.closest('[data-remove-discount]');
      if (removeBtn) { handleRemove(removeBtn); return; }
      const toggleEl = e.target.closest('.cart-discount__toggle');
      if (toggleEl) { handleToggle(toggleEl, e); return; }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.matches('[data-discount-input]')) {
        e.preventDefault();
        handleApply(e.target);
      }
    });

    document.addEventListener('input', function (e) {
      if (!e.target.matches('[data-discount-input]')) return;
      const ctx = getContext(e.target);
      if (ctx.$message?.classList.contains('cart-discount__message--error')) hideMessage(ctx);
    });
  }

  init();

})();